import { useMemo, useRef, useState } from 'react';
import { IconCopy } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import {
    convertNumberToWords,
    parseNumberInput,
    sanitizeNumberInput,
} from './numberToWordsEngine';
import {
    copyTextToClipboard,
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';

const inputClass = 'w-full max-w-md rounded-xl border border-[#ECE8E4] bg-white px-4 py-3 text-[20px] font-semibold tracking-wide text-[#3D3833] outline-none transition-colors placeholder:text-[#A8A099] focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500';

function WordsOutputCard({ label, value, placeholder, onCopy, copyLabel }) {
    return (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
            <div className="mb-2.5 flex items-center justify-between gap-2">
                <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-amber-800 dark:text-amber-300">
                    {label}
                </span>
                <button
                    type="button"
                    onClick={onCopy}
                    disabled={!value}
                    className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-white px-2 py-1 text-[11.5px] font-semibold text-amber-800 transition-colors hover:border-amber-400 disabled:cursor-not-allowed disabled:opacity-50 dark:border-amber-900/50 dark:bg-zinc-900 dark:text-amber-300"
                >
                    <IconCopy size={13} stroke={1.9} />
                    {copyLabel}
                </button>
            </div>
            <p className={`min-h-[48px] text-[15px] font-semibold leading-relaxed ${value ? 'text-[#1C1815] dark:text-zinc-100' : 'text-[#A8A099] dark:text-zinc-500'}`}>
                {value || placeholder}
            </p>
        </div>
    );
}

export default function NumberToWordsText() {
    const { t } = useTranslation();
    const usageRecordedRef = useRef(false);

    const [numInput, setNumInput] = useState('');

    const { parsed, english, bengali } = useMemo(
        () => convertNumberToWords(numInput),
        [numInput],
    );

    const hasInput = numInput.length > 0;
    const hasValidInput = parsed !== null;
    const hasOutput = hasValidInput && (english.length > 0 || bengali.length > 0);
    const hasError = hasInput && !hasValidInput;

    const recordUsageOnce = () => {
        if (usageRecordedRef.current) return;
        usageRecordedRef.current = true;
        recordToolUsage('text/number-to-words', { category: 'text' });
    };

    const handleInputChange = (value) => {
        const next = sanitizeNumberInput(value);
        setNumInput(next);
        if (parseNumberInput(next) !== null) recordUsageOnce();
    };

    const copyText = (text, successKey = 'numberToWordsText.copied') => {
        if (!text) return;

        const showSuccess = () => {
            notifications.show({
                title: t('common.success'),
                message: t(successKey),
                color: 'green',
            });
        };

        if (copyTextToClipboard(text)) {
            showSuccess();
            return;
        }

        void navigator.clipboard?.writeText(text)
            .then(showSuccess, () => {
                notifications.show({
                    title: t('common.error'),
                    message: t('numberToWordsText.copyFailed'),
                    color: 'red',
                });
            });
    };

    const handleReset = () => {
        setNumInput('');
        usageRecordedRef.current = false;
    };

    const status = deriveToolStatus({
        hasInput,
        canRun: hasValidInput,
        hasResult: hasOutput,
        hasError,
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`numberToWordsText.howTo.step${i}`)),
        [t],
    );

    const statusText = getToolStatusText(status, t, {
        ready: t('numberToWordsText.statusReady'),
        success: t('numberToWordsText.statusSuccess'),
        error: t('numberToWordsText.errors.invalid'),
    });

    return (
        <ToolPageLayout
            slug="text/number-to-words"
            subtitleKey="numberToWordsText.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('numberToWordsText.inputTitle')}
            howToSteps={howToSteps}
            onReset={hasInput ? handleReset : undefined}
            controls={(
                <ToolCard title={t('numberToWordsText.numberTitle')}>
                    <p className="mb-3 text-[12px] leading-relaxed text-[#6B635C] dark:text-zinc-400">
                        {t('numberToWordsText.inputHint')}
                    </p>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={numInput}
                        onChange={(e) => handleInputChange(e.target.value)}
                        placeholder={t('numberToWordsText.placeholder')}
                        className={inputClass}
                    />
                    {hasValidInput ? (
                        <p className="mt-2 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                            {t('numberToWordsText.parsedValue', { value: toBnDigits(parsed) })}
                        </p>
                    ) : null}
                </ToolCard>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('numberToWordsText.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('numberToWordsText.previewEmpty')}
                    idleHint={t('numberToWordsText.previewEmptyHint')}
                    error={hasError ? t('numberToWordsText.errors.invalid') : null}
                    showIdle={!hasInput && !hasError}
                >
                    {hasValidInput ? (
                        <div className="grid h-full min-h-0 grid-cols-1 gap-3 sm:grid-cols-2">
                            <WordsOutputCard
                                label={t('numberToWordsText.englishLabel')}
                                value={english}
                                placeholder={t('numberToWordsText.englishPlaceholder')}
                                copyLabel={t('common.copy')}
                                onCopy={() => copyText(english)}
                            />
                            <WordsOutputCard
                                label={t('numberToWordsText.bengaliLabel')}
                                value={bengali}
                                placeholder={t('numberToWordsText.bengaliPlaceholder')}
                                copyLabel={t('common.copy')}
                                onCopy={() => copyText(bengali)}
                            />
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
